import { Button, Flex, FormControl, FormLabel, IconButton, Input, InputGroup, InputRightElement, List, ListItem } from '@chakra-ui/react'
import { Container } from '@components/Container'
import { ArrowLeftSimpleIcon, ArrowRightSimpleIcon } from '@src/icons'
import { OptionType, PartnersContext } from '@src/react-context/partners-context'
import { Select } from 'chakra-react-select'
import { useContext, useEffect, useRef } from 'react'
import { MdClose } from 'react-icons/md'
import { states } from './states'

const UfsPartners = () => {
  const {
    stateSelected,
    setStateSelected,
    partners,
    setPartnersFiltered,
    setCity,
    city,
    setNeighborhood,
    neighborhood,
    setSearch,
    search,
    handleFilter,
    handleClearFields,
    citiesOptions,
    setCitiesOptions,
    neighborhoodOptions,
    setNeighborhoodOptions,
    setPartnersSearch
  } = useContext(PartnersContext)
  const listRef = useRef<HTMLUListElement>(null)
  const isUfSelected = (uf: string) => stateSelected.acronym === uf
  const handleNextUf = () => {
    const nextIndex = states.findIndex(state => state.acronym === stateSelected.acronym) + 1
    if (nextIndex < states.length) setStateSelected(states[nextIndex])
  }

  const handlePrevUf = () => {
    const prevIndex = states.findIndex(state => state.acronym === stateSelected.acronym) - 1
    if (prevIndex >= 0) setStateSelected(states[prevIndex])
  }

  const citiesOptionsBuilder = () => {
    const options: Array<OptionType> = [];

    partners.map(partner => {
      if (options.some(option => option.value == partner.city && option.label == partner.city)) return;

      options.push({
        value: partner.city,
        label: partner.city
      })
    })

    setCitiesOptions(options);
  }

  const buildCitiesWithNeightborhood = () => {
    const citiesWithNeightborhood: {
      [cidade: string]: Array<{ value: string; label: string }>
    } = {};

    partners.forEach(partner => {
      if (!citiesWithNeightborhood[partner.city]) {
        citiesWithNeightborhood[partner.city] = [];
      }

      if (!citiesWithNeightborhood[partner.city].some(b => b.value === partner.neighborhood)) {
        citiesWithNeightborhood[partner.city].push({
          value: partner.neighborhood,
          label: partner.neighborhood
        });
      }
    });

    return citiesWithNeightborhood;
  }

  const neighborhoodOptionsBuilder = (neighborhood: string) => {
    setNeighborhoodOptions(buildCitiesWithNeightborhood()[neighborhood])
  }

  const clearSearch = () => {
    setSearch('');
    setPartnersSearch([]);
  }

  const handleSelectCity = (value: string) => {
    setCity(value);
    setNeighborhood('');
    neighborhoodOptionsBuilder(value);
    clearSearch();

    const partnersFilteredByCity = partners.filter(
      (partner) => partner.city === value
    );

    setPartnersFiltered(partnersFilteredByCity);
  }

  useEffect(() => {
    setSearch('');
    setCity('');
    setNeighborhood('');
    setNeighborhoodOptions([]);
    setPartnersFiltered(partners);
  }, [stateSelected]);

  useEffect(() => {
    const listItem = listRef.current?.querySelector(`li[data-uf='${stateSelected.acronym}']`)
    listItem?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [stateSelected])

  useEffect(() => {
    citiesOptionsBuilder();
    buildCitiesWithNeightborhood();
  }, [partners])

  const handleSelectNeigthborhood = (neighborhood: string) => {
    setNeighborhood(neighborhood);
    clearSearch();

    const partnerFiltered = partners.filter(
      (partner) => partner.neighborhood == neighborhood && partner.city == city
    );

    setPartnersFiltered(partnerFiltered);
  }

  return (
    <>
      <Container direction={'column'}>
        <Flex direction="row" width="100%" alignItems="center" gridGap={'1rem'} padding={'1rem 0'}>
          <IconButton
            icon={<ArrowLeftSimpleIcon />}
            variant="unstyled"
            aria-label="next-button"
            backgroundColor="white"
            onClick={handlePrevUf}
          />
          <List
            ref={listRef}
            display={'flex'}
            width={'100%'}
            gridGap={'1rem'}
            justifyContent={'space-between'}
            overflow={'hidden'}
          >
            {states.map(state => (
              <ListItem
                key={state.acronym}
                data-uf={state.acronym}
                fontWeight={isUfSelected(state.acronym) ? 'semibold' : 'normal'}
                color={isUfSelected(state.acronym) ? 'blue.500' : 'GrayText'}
                borderBottom={isUfSelected(state.acronym) ? '2px solid #0D71F0' : 'none'}
                cursor={'pointer'}
                onClick={() => setStateSelected(state)}
              >
                {' '}
                {state.acronym}{' '}
              </ListItem>
            ))}
          </List>
          <IconButton
            icon={<ArrowRightSimpleIcon />}
            variant="unstyled"
            aria-label="next-button"
            backgroundColor="white"
            onClick={handleNextUf}
          />
        </Flex>
        
        <Flex
          w="full"
          gap={{ base: 4, md: 8 }}
          direction={{ base: 'column', md: 'row' }}
          justifyContent="center"
          alignItems={{ base: 'stretch', md: 'center' }}
          padding={{ base: '1rem', md: '1rem' }}
        >
          <FormControl w="100%" flex={1}>
            <FormLabel>Nome da loja</FormLabel>
            <InputGroup>
              <Input
                size="md"
                placeholder="Nome da loja"
                value={search}
                onChange={handleFilter}
              />
              {search && (
                <InputRightElement>
                  <Button
                    size="sm"
                    variant="ghost"
                    backgroundColor="transparent"
                    _hover={{ backgroundColor: 'transparent' }}
                    _active={{ backgroundColor: 'transparent' }}
                    onClick={() => setSearch('')}
                    aria-label="Limpar pesquisa"
                    p={0}
                    minW="auto"
                  >
                    {MdClose({ size: 18 })}
                  </Button>
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>

          <FormControl w="100%" flex={1}>
            <FormLabel>Cidade</FormLabel>
            <Select
              size="md"
              options={citiesOptions}
              placeholder="Cidade"
              value={city ? { value: city, label: city } : null}
              onChange={(option) => option && handleSelectCity(option.value)}
            />
          </FormControl>

          <FormControl w="100%" flex={1}>
            <FormLabel>Bairro</FormLabel>
            <Select
              size="md"
              options={neighborhoodOptions}
              placeholder="Bairros"
              value={neighborhood ? { value: neighborhood, label: neighborhood } : null}
              onChange={(option) => option && handleSelectNeigthborhood(option.value)}
            />
          </FormControl>

          <Button
            colorScheme="gray"
            variant="outline"
            onClick={handleClearFields}
            alignSelf={{ base: 'stretch', md: 'flex-end' }}
            minW="80px"
            w={{ base: '100%', md: 'auto' }}
          >
            Limpar
          </Button>
        </Flex>
      </Container>


    </>
  )
}

export default UfsPartners
